// src/pages/TrackOrder.jsx
import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { base_url } from '../utils/axiosConfig';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StoreIcon from '@mui/icons-material/Store';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CancelIcon from '@mui/icons-material/Cancel';

// Order Status Stages
const statusStages = ['Ordered', 'In Transit', 'Out for Delivery', 'Delivered'];

const statusIcons = {
  Ordered: <StoreIcon />,
  'In Transit': <LocalShippingIcon />,
  'Out for Delivery': <DeliveryDiningIcon />,
  Delivered: <DoneAllIcon />,
};

const StyledTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
  padding: '30px 20px',
  marginTop: theme.spacing(4),
  borderRadius: theme.spacing(1),
  background: '#f9f9f9',
  overflowX: 'auto',
}));

const StepCircle = styled(Box)(({ active, completed }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: completed ? '#4caf50' : active ? '#1976d2' : '#ccc',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
}));

const BackgroundLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: 40,
  right: 40,
  height: 4,
  backgroundColor: '#ccc',
  zIndex: 1,
  transform: 'translateY(-50%)',
}));

const ProgressLine = styled(Box)(({ theme, progress }) => ({
  position: 'absolute',
  top: '50%',
  left: 40,
  height: 4,
  backgroundColor: '#4caf50',
  zIndex: 1,
  transform: 'translateY(-50%)',
  width: `${progress}%`,
  transition: 'width 0.3s ease-in-out',
}));

const OrderCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

const FormBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  maxWidth: 600,
  margin: 'auto',
  padding: theme.spacing(4),
}));

const TrackOrder = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackedOrder, setTrackedOrder] = useState(null);
  const [trackError, setTrackError] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const navigate = useNavigate();

  const handleTrackOrder = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) {
      setTrackError('Tracking number is required');
      return;
    }
    setTrackLoading(true);
    setTrackError(null);
    setTrackedOrder(null);

    try {
      const token = JSON.parse(localStorage.getItem('customer'))?.token;
      if (!token) throw new Error('Authentication required');

      const response = await axios.post(
        `${base_url}user/trackorder`,
        { trackingNumber: trackingNumber.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data) {
        setTrackedOrder(response.data);
        toast.success('Order found!');
      } else {
        setTrackError('No order found with this tracking number');
      }
    } catch (error) {
      console.error('Track order error:', error);
      const msg = error.response?.data?.message || 'Failed to track order';
      setTrackError(msg);
      toast.error(msg);
    } finally {
      setTrackLoading(false);
    }
  };

  const getActiveStepIndex = (status) => {
    const index = statusStages.indexOf(status);
    return index === -1 ? 0 : index; // Default to 0 if status is invalid
  };

  const calculateProgress = (status) => {
    const totalStages = statusStages.length;
    const activeIndex = getActiveStepIndex(status);
    return ((activeIndex + 1) / totalStages) * 100; // Calculate progress percentage
  };

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: 'fit-content',
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            transition: '0.3s',
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#1976d236' },
          }}
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon sx={{ mr: 1, color: '#1976d2' }} />
          <Typography sx={{ fontWeight: 500, color: '#1976d2' }}>Back</Typography>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mx: 'auto' }}>
          Track Your Order
        </Typography>
      </Box>

      <FormBox>
        <form onSubmit={handleTrackOrder}>
          <TextField
            label="Tracking Number"
            name="trackingNumber"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            required
            error={!!trackError}
            helperText={trackError}
          />
          <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }} disabled={trackLoading}>
            {trackLoading ? <CircularProgress size={24} /> : 'Track Order'}
          </Button>
        </form>

        {trackedOrder && (
          <OrderCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Details
              </Typography>
              <Typography fontWeight="bold">Tracking Number: {trackedOrder.trackingNumber}</Typography>
              <Typography variant="body2" color="text.secondary">
                Shipping Info:{' '}
                {trackedOrder.shippingInfo
                  ? `${trackedOrder.shippingInfo.firstName} ${trackedOrder.shippingInfo.lastName}, ${trackedOrder.shippingInfo.address}, ${trackedOrder.shippingInfo.city}, ${trackedOrder.shippingInfo.state}, Landmark: ${trackedOrder.shippingInfo.landmark || 'N/A'}`
                  : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Items:{' '}
                {trackedOrder.orderItems.map((item) => `${item.product.title} (x${item.quantity})`).join(', ')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total: ${trackedOrder.orderItems
                  .reduce((acc, item) => acc + item.price * item.quantity, 0)
                  .toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ordered On:{' '}
                {new Date(trackedOrder.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>

              <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  Order Status
                </Typography>

                {trackedOrder.orderStatus === 'Cancelled' ? (
                  <Box display="flex" alignItems="center" color="error.main">
                    <CancelIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">Order Cancelled</Typography>
                  </Box>
                ) : (
                  <StyledTrack>
                    <BackgroundLine />
                    <ProgressLine progress={calculateProgress(trackedOrder.orderStatus)} />
                    {statusStages.map((stage, index) => {
                      const isCompleted = index <= getActiveStepIndex(trackedOrder.orderStatus);
                      const isActive = index === getActiveStepIndex(trackedOrder.orderStatus);
                      return (
                        <Box key={stage} display="flex" flexDirection="column" alignItems="center" zIndex={2}>
                          <Tooltip title={stage}>
                            <StepCircle active={isActive} completed={isCompleted}>
                              {statusIcons[stage]}
                            </StepCircle>
                          </Tooltip>
                          <Typography variant="caption" sx={{ mt: 1 }}>
                            {stage}
                          </Typography>
                        </Box>
                      );
                    })}
                  </StyledTrack>
                )}
              </Box>
            </CardContent>
          </OrderCard>
        )}
      </FormBox>
    </Container>
  );
};

export default TrackOrder;